import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 按页面实际高度自适应每页行数，让表格正好填满视口、底部不留空白。
 *
 * 实现：容器挂载时（callback ref，兼容 `{rows && <div ref=…>}` 的条件渲染）
 * 实测「表头底边位置 + 真实行高 + 分页条预留」，
 * 用 ResizeObserver + window.resize 实时重算。
 *
 * 用法：
 *   const { containerRef, pageSize } = useAutoPageSize();
 *   <div ref={containerRef}>
 *     <table>…rows.slice((page-1)*pageSize, page*pageSize)…</table>
 *     <Pager pageSize={pageSize} … />
 *   </div>
 */
export function useAutoPageSize(opts?: { min?: number; max?: number; fallbackRow?: number; rowSelector?: string }) {
  const min = opts?.min ?? 4;
  const max = opts?.max ?? 200;
  const fallbackRow = opts?.fallbackRow ?? 44;
  // 行元素选择器：默认表格行；div 列表可传自定义选择器以测真实行高/起点
  const rowSelector = opts?.rowSelector ?? "tbody tr";

  const [pageSize, setPageSize] = useState(10);
  const cleanupRef = useRef<(() => void) | null>(null);

  const containerRef = useCallback((el: HTMLDivElement | null) => {
    // 容器卸载或更换：清理旧监听
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!el) return;

    let raf = 0;
    // 是否传了自定义行选择器（div 列表模式）：启用「实测行高/起点」精确路径，
    // 不传时保持原表格路径不变（避免影响既有调用）。
    const custom = !!opts?.rowSelector;

    const measure = () => {
      const table = el.querySelector("table");
      const thead = table?.querySelector("thead");
      const rowsEls = el.querySelectorAll(custom ? rowSelector : "tbody tr");
      const firstRow = rowsEls[0] as HTMLElement | undefined;

      let rowH = firstRow ? firstRow.getBoundingClientRect().height : fallbackRow;
      // div 列表带 grid gap 时，用「第二行顶 − 第一行顶」把间距计入，避免每行少算导致溢出
      if (custom && rowsEls.length >= 2) {
        const step = rowsEls[1].getBoundingClientRect().top - rowsEls[0].getBoundingClientRect().top;
        if (step > 0) rowH = step;
      }

      // 行区起点：有表头用表头底边；自定义模式优先用第一行顶边（最准）；
      // 否则沿用「容器顶 + 估算表头高 40」（原表格路径）。
      // 叠加滚动容器 scrollTop，让测量与滚动位置无关（避免反馈循环）。
      const scroller = el.closest(".main");
      const scrolled = scroller ? scroller.scrollTop : 0;
      const rowsTop = (thead
        ? thead.getBoundingClientRect().bottom
        : custom && firstRow
          ? firstRow.getBoundingClientRect().top
          : el.getBoundingClientRect().top + 40) + scrolled;

      const pagerH = 52;    // 分页条（含 margin-top）
      const bottomPad = 26; // 底部留白

      const avail = window.innerHeight - rowsTop - pagerH - bottomPad;
      const n = Math.floor(avail / rowH);
      const next = Math.max(min, Math.min(max, n));
      setPageSize((prev) => (prev === next ? prev : next));
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    schedule();
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    window.addEventListener("resize", schedule);
    cleanupRef.current = () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", schedule);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 组件卸载时清理
  useEffect(() => () => { cleanupRef.current?.(); }, []);

  return { containerRef, pageSize };
}
